import { useContext, useEffect, useRef, useState } from 'react'
import DtcTable from './DtcTable'
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { NavContext } from '../../../context/global_context'
import { getRequest, postRequest } from '../../../services/base'
import EditMappings from './EditMappings'
import Button from '../../common/Button'
import {
  getSessionStorageData,
  setSessionStorageData,
} from '../../../utils/SessionStorage'
import { RESET_SELECTED_DTC, TatContext } from './context/context'
import { Checkbox, Form, Modal, Row, Segmented, Space, Typography } from 'antd'
import InputText from '../../common/InputText'
import SelectInput from '../../common/SelectInput'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import useDebouncedValue from '../../../utils/common_function'
import { useIntl } from 'react-intl'
import { notifyError } from '../../../utils/notifications'

const DtcsListing = () => {
  const intl = useIntl()
  const interval = useRef(null)

  let filterData = getSessionStorageData('dtcs_list')
  let { vehicle } = useParams()
  const navigate = useNavigate()
  const [modal, contextHolder] = Modal.useModal()
  const [form] = Form.useForm()

  const [ecusData, setEcusData] = useState([])
  const [dtcsStatus, setDtcsStatus] = useState({
    enable_mapping: true,
    enable_generate_wire: false,
  })
  const [value, dispatch] = useContext(NavContext)
  const [tatData, tatDataDispatch] = useContext(TatContext)
  // Filters
  const defaultFilters = {
    searchValue: filterData?.searchValue ? filterData?.searchValue : null,
    ecu: filterData?.ecu?.value ? filterData?.ecu : null,
    hasNoConnectorMapping: filterData?.hasNoConnectorMapping ? filterData?.hasNoConnectorMapping : 'yes',
    active: filterData?.active ? filterData?.active : 'yes',
    destinationOnly: filterData?.destinationOnly ? filterData?.destinationOnly : 'no',
  }
  const [filters, setFilters] = useState(defaultFilters)
  const debouncedSearch = useDebouncedValue(filters.searchValue, 500)
  const ignoreDebouncedSearch = useRef(false)

  const [dtcData, setDtcData] = useState([])
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    current_page: 1,
  })

  const currentUrl = useRef(
    `/api/dtcs/?search=${
      filters.searchValue ? filters.searchValue : ''
    }&vehicle=${vehicle}&has_no_con_mapping=${filters.hasNoConnectorMapping}&destination_only=${filters.destinationOnly}&active=${filters.active}&ecu=${
      filters.ecu ? filters.ecu : ''
    }&limit=10`
  )

  useEffect(() => {
    setDefaultValues()
    getEcuData('')
    callDtcsStatusInterval()
    getDtcsStatus()
    dispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header: intl.formatMessage({ id: 'layout.vehicleList' }) + ` / ${vehicle} / DTCs / ` + intl.formatMessage({ id: 'sidebar.taMappings' }),
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: getSessionStorageData('vehicle')?.search
          ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
          : `/vehicles`,
      },
    })

    return () => {
      if (interval.current) {
        clearInterval(interval.current)
      }
    }
  }, [])

  const callDtcsStatusInterval = () => {
    interval.current = setInterval(() => {
      getDtcsStatus()
    }, 10000)
  }

  const setDefaultValues = () => {
    const {searchValue, ecu, hasNoConnectorMapping, active, destinationOnly} = filters

    currentUrl.current = `/api/dtcs/?search=${
      searchValue ? searchValue : ''
    }&vehicle=${vehicle}&has_no_con_mapping=${hasNoConnectorMapping}&destination_only=${destinationOnly}&active=${active}&ecu=${
      ecu?.value ? ecu?.value : ''
    }&limit=10`
    getDtcsData()

    form.setFieldsValue({
      dtcs_search: searchValue,
      select_ecu: ecu,
      has_no_connector_mappings: hasNoConnectorMapping,
      active: active,
      destination_only: destinationOnly,
    })
  }

  useEffect(() => {
    // Ignore "debouncedSearch" change on Reset ONLY if other filters have also changed
    if (ignoreDebouncedSearch.current && filters.searchValue === debouncedSearch) {
      ignoreDebouncedSearch.current = false
      return
    }

    const {searchValue, ecu, hasNoConnectorMapping, active, destinationOnly} = filters
    setSessionStorageData('dtcs_list', filters)

    currentUrl.current = `/api/dtcs/?search=${
      searchValue ? searchValue : ''
    }&vehicle=${vehicle}&has_no_con_mapping=${hasNoConnectorMapping}&destination_only=${destinationOnly}&active=${active}&ecu=${
      ecu?.value ? ecu?.value : ''
    }&limit=10`

    getDtcsData()
  }, [debouncedSearch, filters.ecu, filters.hasNoConnectorMapping, filters.active, filters.destinationOnly])

  const getDtcsData = async () => {
    dispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'dtcs.listing.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    let response = await getRequest(currentUrl.current)
    setDtcData(response?.results)
    setPagination(response?.pagination)
    dispatch({
      type: 'loader',
      data: {},
      notification: {},
    })

    // A 'loader' action resets `notification`, so notify after it, not before.
    if (response?.status === false) {
      notifyError(dispatch, intl.formatMessage({ id: 'dtcs.listing.loadError' }))
    }
  }

  const getEcuData = async (search) => {
    let response = await getRequest(
      `/api/ecu/ecu/?vehicle=${vehicle}&search=${search}`
    )
    // The length check below leaves a failure completely silent, so the failure
    // branch has to be explicit: base.js returns {status: false}, not a rejection.
    if (response?.status === false) {
      notifyError(dispatch, intl.formatMessage({ id: 'dtcs.ecusLoadError' }))
      return
    }
    if (response?.data?.length > 0) {
      setEcusData(response.data)
    }
  }

  const getDtcsStatus = async () => {
    let response = await getRequest(`/api/dtcs/dtcs_status/?vehicle=${vehicle}`)
    setDtcsStatus(response)
  }

  const getPaginatedData = async (url) => {
    let response = await getRequest(url)
    if (response?.status === false) {
      notifyError(dispatch, intl.formatMessage({ id: 'dtcs.listing.loadError' }))
      return
    }
    if (response?.results?.length > 0) {
      setDtcData(response.results)
      setPagination(response.pagination)
    }
  }

  const resetFilterData = () => {
    const searchFilterDefautChanged = filters.searchValue !== null
    const otherFilterDefaultsChanged = filters.ecu !== null || filters.hasNoConnectorMapping !== 'yes' || filters.active !== 'yes' || filters.destinationOnly !== 'no'
    if(searchFilterDefautChanged && otherFilterDefaultsChanged) {
      ignoreDebouncedSearch.current = true
    }
    // Reset State
    setFilters({
      searchValue: null,
      ecu: null,
      hasNoConnectorMapping: 'yes',
      active: 'yes',
      destinationOnly: 'no',
    })
    // Reset Form fields
    form.setFieldsValue({
      dtcs_search: null,
      select_ecu: null,
      has_no_connector_mappings: 'yes',
      active: 'yes',
      destination_only: 'no',
    })
  }

  const generateWires = async () => {
    const res = await postRequest(`/api/wire/generate_wires/`, {
      vehicle: vehicle,
      is_inspection_points_wire: false,
      is_power_distribution_wire: false,
    })
    // The result used to be discarded entirely, so a failed trigger was
    // indistinguishable from a successful one.
    if (res?.status === false) {
      notifyError(dispatch, intl.formatMessage({ id: 'dtcs.listing.generateWiresError' }))
      return
    }
    getDtcsStatus()
  }

  const showDeleteConfirmation = () => {
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: intl.formatMessage({ id: 'dtcs.listing.deleteTitle' }),
      content: <>{intl.formatMessage({ id: 'dtcs.listing.deleteConfirm' })}</>,
      centered: true,
      maskClosable: true,
      okText: intl.formatMessage({ id: 'forms.delete' }),
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      onOk: deleteSelectedDtc,
      width: '26%',
    })
  }

  const deleteSelectedDtc = async () => {
    let payload = { vehicle: vehicle, selected_dtc: tatData.selected_dtc }
    let res = await postRequest(
      `/api/dtcs/delete_dtc/?vehicle=${vehicle}`,
      payload
    )
    if (res.status) {
      getDtcsData()
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'dtcs.listing.deleteSuccess' }),
        },
      })
      tatDataDispatch({ type: RESET_SELECTED_DTC, data: [] })
    } else {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'dtcs.listing.deleteError' }),
        },
      })
    }
  }

  const createNewDtc = () => {
    navigate(`/vehicles/${vehicle}/dtcs/listing/new`)
  }

  const location = useLocation()
  const menuItems = [
    {
      label: intl.formatMessage({ id: 'sidebar.taMappings' }),
      value: `/vehicles/${vehicle}/dtcs/listing`,
    },
    {
      label: intl.formatMessage({ id: 'sidebar.csMappings' }),
      value: `/vehicles/${vehicle}/dtcs/tracer_mapping`,
    },
    {
      label: intl.formatMessage({ id: 'sidebar.csMemos' }),
      value: `/vehicles/${vehicle}/dtcs/memos`,
    },
  ]

  return (
    <div className="page tat-mappings">
      <Row className="title">
        <Segmented
          options={menuItems}
          value={location.pathname}
          onChange={(value) => navigate(value)}
        />
      </Row>
      <Row className="body">
        <Form form={form} className="tat-mappings-form">
          <Space direction="horizontal" size="middle" className="data-filters">
            <InputText
              name="dtcs_search"
              type="search"
              placeholder={intl.formatMessage({ id: 'dtcs.listing.searchPlaceholder' })}
              value={filters.searchValue}
              isRequired={false}
              wrapperClass="search-dtc"
              onChange={(value) => {
                setFilters(prev => ({
                  ...prev,
                  searchValue: value
                }))
              }}
            />

            <SelectInput
              name="select_ecu"
              options={ecusData}
              value={ecusData.filter((option) => option.label === filters.ecu?.label)}
              placeholder={intl.formatMessage({ id: 'dtcs.listing.selectEcu' })}
              allowClear={true}
              isRequired={false}
              wrapperClass="select-ecu"
              onChange={(value) => {
                setFilters(prev => ({
                  ...prev,
                  ecu: value
                }))
              }}
            />

            <Space
              direction="horizontal"
              size="middle"
              className="ant-checkbox-group ant-form-item-inline"
            >
              <Checkbox
                data-testid="dtc-filter-no-mappings"
                name="has_no_connector_mappings"
                checked={filters.hasNoConnectorMapping === 'yes'}
                onChange={(e) => {
                  const value = e.target.checked ? 'yes' : 'no'
                  setFilters(prev => ({
                    ...prev,
                    hasNoConnectorMapping: value
                  }))
                }}
              >
                {intl.formatMessage({ id: 'dtcs.listing.hasNoConnectorMappings' })}
              </Checkbox>

              <Checkbox
                data-testid="dtc-filter-active"
                name="active"
                checked={filters.active === 'yes'}
                onChange={(e) => {
                  const value = e.target.checked ? 'yes' : 'no'
                  setFilters(prev => ({
                    ...prev,
                    active: value
                  }))
                }}
              >
                {intl.formatMessage({ id: 'generic.active' })}
              </Checkbox>

              <Checkbox
                data-testid="dtc-filter-destination-only"
                name="destination_only"
                checked={filters.destinationOnly === 'yes'}
                onChange={(e) => {
                  const value = e.target.checked ? 'yes' : 'no'
                  setFilters(prev => ({
                    ...prev,
                    destinationOnly: value
                  }))
                }}
              >
                {intl.formatMessage({ id: 'dtcs.listing.destinationOnly' })}
              </Checkbox>
            </Space>

            <Button
              type="link"
              className="link-button"
              onClick={resetFilterData}
              testId="dtc-clear-filters"
            >
              {intl.formatMessage({ id: 'dtcs.listing.clearFilters' })}
            </Button>
          </Space>

          <Space direction="horizontal" size="middle" className="crud-panel">
            <Space size="middle">
              <EditMappings
                selectedDtcs={tatData?.selected_dtc}
                sid={vehicle}
                enableMapping={dtcsStatus.enable_mapping}
                filters={{
                  searchValue: filters.searchValue,
                  ecu: filters.ecu,
                  hasNoConnectorMapping: filters.hasNoConnectorMapping,
                  active: filters.active,
                  destinationOnly: filters.destinationOnly,
                }}
              />

              <Button
                className={`multiline-button danger ${tatData.selected_dtc < 1 && 'disabled'}`}
                onClick={() => showDeleteConfirmation()}
                testId="dtc-delete-selected"
              >
                {intl.formatMessage({ id: 'dtcs.listing.deleteSelected' })}
              </Button>
            </Space>

            <Space size="middle">
              <Button
                className={`multiline-button`}
                disabled={!dtcsStatus.enable_generate_wire}
                onClick={() => generateWires()}
                testId="dtc-regenerate-graph"
              >
                {intl.formatMessage({ id: 'dtcs.listing.regenerateGraph' })}
              </Button>

              <Button type='primary' className="multiline-button" onClick={() => createNewDtc()} testId="dtc-create">{intl.formatMessage({ id: 'dtcs.listing.createNew' })}</Button>
            </Space>
          </Space>
        </Form>

        <DtcTable
          dtcs={dtcData}
          pagination={pagination}
          getPaginatedData={getPaginatedData}
          vehicleSid={vehicle}
          hasNoConnectorMapping={filters.hasNoConnectorMapping}
        />
      </Row>

      {contextHolder}
    </div>
  )
}

export default DtcsListing
